import type { BuilderIngredient, PizzaDraft } from './pizza-builder'
import { basesFor, customPizzaNameOf, sizesOf } from './pizza-builder'
import type { PizzaConfig } from './pizza-config'
import { tt } from './i18n/translation-memory'

/**
 * A stored custom pizza, back in the builder - `FRONTEND.md` §9.465, owner answer to #36.
 *
 * **The owner's report** was *"when we have ccustom pizza ordered online, and they want to edit
 * pizza, we cant edit it, its like normal položka and not editable pizza"*. §9.440 answered the half
 * that needed no decision: the line now says what the pizza is made of. This is the other half -
 * clicking it open - and it waited on a money question, which `OWNER-QUESTIONS.md` #36 answered:
 * **"Re-price from today's menu"**.
 *
 * **That answer is what makes this simple.** `order_item_parts` records each piece's name, quantity
 * and the money it was sold for, and no ingredient id: a topping's stored price is the **surcharge
 * at that size**, not a catalogue price, which is why `V24` writes no `product_id` for one. So a
 * reopened pizza cannot be rebuilt at its old price without re-deriving surcharges that may no
 * longer exist - and it does not have to be. The draft is reconstructed, the builder prices it from
 * today's config exactly as it prices a new one, and the operator sees the figure before they accept
 * it.
 *
 * **Matched by name, and it refuses rather than guesses.** This is the trade `V24`'s own author
 * wrote the warning against - *a control that silently rebuilds a pizza from its name* - and the
 * warning is honoured by the refusal: one unmatched piece and the builder does not open, naming what
 * it could not find. A pizza that reopens missing a topping is the failure that costs money, because
 * nothing on the screen says a topping went missing.
 *
 * **Deliberately not a stored draft.** Recording the ingredient ids would be exact, and would leave
 * every pizza already on the owner's stack - the ones they are asking about - still unopenable. A
 * name match that refuses covers both, and the day a franchise renames an ingredient it degrades to
 * the behaviour that exists today rather than to a wrong pizza.
 */

/** A stored piece of a composed line: the two fields `ComposedPart` keeps. */
export interface StoredPart {
  name: string
  quantity: number
}

/** What a reopen attempt came to. Exactly one of the two is set. */
export interface ReopenedPizza {
  /** The draft to open the builder with, or `null` when it could not be rebuilt. */
  draft: PizzaDraft | null
  /** Why not, in the operator's words. Empty when there is a draft. */
  refusal: string
}

/** Trimmed, case-folded and with runs of whitespace collapsed. Diacritics are left alone. */
function key(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * The draft a stored custom pizza came from, or the reason it cannot be had.
 *
 * The parts arrive in the order `pizzaParts` emits and `order_item_parts.position` preserves: the
 * size, then the base when one was chosen, then the toppings. The base is **optional** in that
 * sequence - `pizzaParts` pushes it only when the draft's base is one the config offers - so
 * position 1 is read as a base when it matches one and as a topping otherwise, rather than assumed.
 *
 * **§9.680 - the name comes back too.** It is not a part: it lives in the stored line's own name, `Vlastná pizza
 * „Moja“ – 32 cm (...)`, so `lineName` is **required** - a reopen that forgot it would compile and silently drop
 * what the customer called their pizza, which is §9.518's optional-argument trap.
 */
export function draftFromParts(
  parts: readonly StoredPart[],
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
  lineName: string,
): ReopenedPizza {
  if (parts.length === 0) {
    // §10A.202's own note covers this on the screen: the 488 lines placed before `V24` have no
    // pieces stored, so there is nothing to rebuild and saying so is the whole of the answer.
    return { draft: null, refusal: tt('Zloženie tejto pizze sa neukladalo, nedá sa otvoriť.') }
  }

  const sizes = sizesOf(config)
  const first = parts[0]
  const size = sizes.find((candidate) => key(candidate.label) === key(first?.name ?? ''))
  if (size === undefined) {
    return {
      draft: null,
      // Named, because *the size is gone* and *the ingredient is gone* are different things for the
      // person holding the phone: one is a size the franchise has switched off.
      refusal: `${tt('Táto veľkosť sa už nenabíja')}: ${first?.name ?? ''}`,
    }
  }

  const rest = parts.slice(1)
  /* §9.681 - the list the builder offers, which is what `pizzaParts` wrote the base part from. This read
   * `config.bases`, so on a franchise whose bases are *Pizza ingrediencie* rows the stored base part was not
   * recognised as a base - and since the rows are in `ingredients` too, it was read back as a **topping**: a
   * reopened pizza with no base and an extra *Pomodoro pelato základ* on top. */
  const bases = basesFor(config, ingredients)
  const second = rest[0]
  const base =
    second === undefined
      ? undefined
      : bases.find((candidate) => key(candidate.label) === key(second.name))
  const toppings = base === undefined ? rest : rest.slice(1)

  const byName = new Map<string, BuilderIngredient>()
  for (const ingredient of ingredients) {
    byName.set(key(ingredient.name), ingredient)
  }

  const ingredientIds: number[] = []
  const missing: string[] = []
  for (const part of toppings) {
    const ingredient = byName.get(key(part.name))
    if (ingredient === undefined) {
      missing.push(part.name)
      continue
    }
    // An id twice is a double portion - §9.43, and `PizzaDraft.ingredientIds` is the one structure
    // that says so. `quantity` on the stored part is the same fact written the other way round.
    for (let each = 0; each < Math.max(1, part.quantity); each += 1) {
      ingredientIds.push(ingredient.id)
    }
  }

  if (missing.length > 0) {
    /* **The refusal is the point of this function.** Opening the builder without these would produce a
     * pizza that looks complete and is not, and the operator would send it. `V24`'s comment says the
     * same thing about its own `product_id`: a name is not an identity. */
    return {
      draft: null,
      refusal: `${tt('Tieto ingrediencie sa už nenabíjajú')}: ${missing.join(', ')}`,
    }
  }

  const name = customPizzaNameOf(lineName)
  return {
    draft: {
      sizeCm: size.cm,
      baseValue: base === undefined ? null : base.value,
      ingredientIds,
      // Absent rather than empty for an unnamed pizza, so the draft is the one `pizzaParts` came from.
      ...(name === '' ? {} : { name }),
    },
    refusal: '',
  }
}
